import React, { useEffect, useRef, useState } from 'react';

const cx = (...a) => a.filter(Boolean).join(' ');

const sizeMap = {
    xs: 'h-6 w-6 text-xs',
    sm: 'h-8 w-8 text-sm',
    md: 'h-10 w-10 text-base',
    lg: 'h-12 w-12 text-lg',
    xl: 'h-16 w-16 text-xl',
};

const colorMap = {
    primary: 'bg-blue-500 text-white',
    neutral: 'bg-gray-400 text-white',
    success: 'bg-green-500 text-white',
    warning: 'bg-amber-500 text-white',
    danger: 'bg-red-500 text-white',
    purple: 'bg-purple-500 text-white',
    pink: 'bg-pink-500 text-white',
    indigo: 'bg-indigo-500 text-white',
};

const borderMap = {
    none: 'border-0',
    sm: 'border-2',
    md: 'border-4',
};

// Generate initials from name
const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Get consistent color based on name
const getColorFromName = (name) => {
    if (!name) return 'neutral';
    const colors = ['primary', 'success', 'warning', 'danger', 'purple', 'pink', 'indigo'];
    const hash = name.charCodeAt(0) + name.charCodeAt(name.length - 1);
    return colors[hash % colors.length];
};

export default function Avatar({
    src,
    alt = '',
    name = '',
    size = 'md',
    color,
    border = 'none',
    borderColor = 'white',
    className = '',
    style,
    onClick,
    uploadable = false,
    accept = 'image/*',
    uploadLabel,
    disabled = false,
    onUpload,
    onUploadError,
}) {
    const [localSrc, setLocalSrc] = useState('');
    const inputRef = useRef(null);
    const objectUrlRef = useRef('');

    useEffect(() => {
        return () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = '';
            }
        };
    }, []);

    const initials = getInitials(name);
    const bgColor = color || getColorFromName(name);
    const borderClass = borderMap[border] || borderMap.none;
    const resolvedSrc = src || localSrc;
    const isUploadable = uploadable && !disabled;
    const overlayLabel = uploadLabel || (resolvedSrc ? 'Change' : 'Upload');

    const classes = cx(
        'inline-flex items-center justify-center rounded-full font-semibold flex-shrink-0',
        uploadable ? 'relative overflow-hidden group' : '',
        sizeMap[size] || sizeMap.md,
        resolvedSrc ? '' : colorMap[bgColor] || colorMap.neutral,
        borderClass && `${borderClass} border-${borderColor}`,
        className
    );

    const handleUploadClick = () => {
        if (!isUploadable) return;
        if (inputRef.current) inputRef.current.click();
    };

    const handleFileChange = (event) => {
        const file = event.target?.files?.[0];
        if (!file) return;
        if (!file.type || !file.type.startsWith('image/')) {
            if (onUploadError) onUploadError('Only image files are supported.');
            event.target.value = '';
            return;
        } 

        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = '';
        }

        const nextUrl = URL.createObjectURL(file);
        objectUrlRef.current = nextUrl;
        setLocalSrc(nextUrl);
        if (onUpload) onUpload(file);
    };

    return (
        <div
            className={classes}
            style={style}
            onClick={onClick}
            role={resolvedSrc ? undefined : 'img'}
            aria-label={alt || name || 'Avatar'}
        >
            {resolvedSrc ? (
                <img
                    src={resolvedSrc}
                    alt={alt || name}
                    className="h-full w-full rounded-full object-cover"
                    onError={(e) => {
                        // Fallback to initials if image fails to load
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                    }}
                />
            ) : null}
            <span
                className={cx(
                    'h-full w-full items-center justify-center rounded-full bg-inherit',
                    resolvedSrc ? 'hidden' : 'flex'
                )}
            >
                {initials}
            </span>
            {isUploadable && (
                <>
                    <button
                        type="button"
                        className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 text-xs font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
                        onClick={handleUploadClick}
                        aria-label={overlayLabel}
                    >
                        {overlayLabel}
                    </button>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={accept}
                        onChange={handleFileChange}
                        className="hidden"
                    />
                </>
            )}
        </div>
    );
}